import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaCalendarAlt, 
  FaUser, 
  FaTag, 
  FaArrowRight, 
  FaSearch,
  FaTimes,
  FaBookOpen,
  FaShare,
  FaBookmark,
  FaTwitter,
  FaLinkedin,
  FaEnvelope,
  FaLink,
  FaCheck,
  FaLightbulb,
  FaShieldAlt,
  FaRocket,
  FaCloud,
  FaMobileAlt
} from 'react-icons/fa';
import { useState, useEffect } from 'react';

// ─── Blog Images ───
const blogImages = {
  1: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=500&fit=crop',
  2: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=500&fit=crop',
  3: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=500&fit=crop',
  4: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
  5: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=500&fit=crop',
};

// ─── Blog Posts from Content Pack ───
const blogPosts = [
  {
    id: 1,
    title: 'What Should You Look for When Replacing a SACCO Core System?',
    excerpt: 'A practical guide covering functionality, migration, integrations, security, configurability, support, reporting and the ability to evolve with the SACCO.',
    content: `
      <p>Replacing a core system is one of the most significant technology decisions a SACCO can make. The right system can transform operations, while the wrong choice can create years of challenges. Here's what to look for.</p>
      
      <h3>Functionality That Matches Your Operations</h3>
      <p>The system must support your specific business processes. Look for:</p>
      <ul>
        <li>Member management and account handling</li>
        <li>Savings and fixed deposit processing</li>
        <li>Loan origination, disbursement and repayment</li>
        <li>Share capital management and dividend calculations</li>
        <li>Financial accounting and general ledger</li>
        <li>Comprehensive reporting and analytics</li>
      </ul>
      
      <h3>Data Migration Capabilities</h3>
      <p>Your historical data is valuable. The new system should support:</p>
      <ul>
        <li>Migration of member information and account balances</li>
        <li>Loan history and repayment records</li>
        <li>Financial transactions and audit trails</li>
        <li>Data validation and quality checks</li>
      </ul>
      
      <h3>Integration Requirements</h3>
      <p>Modern SACCOs need connectivity to the wider financial ecosystem:</p>
      <ul>
        <li>M-PESA and mobile money integration</li>
        <li>Bank API connectivity for transaction notifications</li>
        <li>CRB integration for credit referencing</li>
        <li>Mobile apps and USSD channels</li>
        <li>SMS gateways for member communication</li>
      </ul>
      
      <h3>Configurability & Extensibility</h3>
      <p>The system should adapt to your way of working, not the other way around. Look for:</p>
      <ul>
        <li>Configurable loan products and charges</li>
        <li>Flexible approval workflows</li>
        <li>Customizable reporting and dashboards</li>
        <li>Ability to extend functionality as you grow</li>
      </ul>
      
      <h3>Implementation & Support</h3>
      <p>A successful implementation requires more than software:</p>
      <ul>
        <li>Requirements gathering and process analysis</li>
        <li>Data migration planning and execution</li>
        <li>User training and change management</li>
        <li>Testing, commissioning and go-live support</li>
        <li>Ongoing support and evolution</li>
      </ul>
      
      <p>Choosing the right core system partner means finding a provider who understands SACCO operations, has proven experience, and can support your organization as it grows.</p>
    `,
    date: 'August 15, 2026',
    author: 'Micropoint Systems',
    category: 'Guide',
    readTime: '8 min read',
    featured: true,
    icon: FaRocket
  },
  {
    id: 2,
    title: 'Why Loan Management Is More Than Disbursement and Repayment',
    excerpt: 'A practical look at appraisal, schedules, charges, top-ups, restructuring, collateral, guarantors, repayments, reconciliation and accounting.',
    content: `
      <p>Loan management is often reduced to two activities: disbursement and repayment. In practice, effective loan management involves a much broader set of capabilities.</p>
      
      <h3>Before Disbursement</h3>
      <p>Good loan management starts before any money changes hands:</p>
      <ul>
        <li>Loan appraisal and credit assessment</li>
        <li>Collateral and guarantor management</li>
        <li>Product selection and configuration</li>
        <li>Approval workflows and authorization</li>
        <li>Disbursement scheduling and execution</li>
      </ul>
      
      <h3>During the Loan Lifecycle</h3>
      <p>Managing active loans requires ongoing attention:</p>
      <ul>
        <li>Repayment scheduling and tracking</li>
        <li>Interest calculations and accruals</li>
        <li>Charges and fee management</li>
        <li>Communication with borrowers</li>
        <li>Performance monitoring</li>
      </ul>
      
      <h3>When Things Change</h3>
      <p>Flexibility is essential for responding to borrower circumstances:</p>
      <ul>
        <li>Loan top-ups and additional financing</li>
        <li>Restructuring and rescheduling</li>
        <li>Extensions and modifications</li>
        <li>Write-offs and provisions</li>
      </ul>
      
      <h3>After the Loan</h3>
      <p>The loan lifecycle continues beyond full repayment:</p>
      <ul>
        <li>Loan closure and final accounting</li>
        <li>Performance analysis and reporting</li>
        <li>Portfolio quality assessment</li>
        <li>Historical records and audit trails</li>
      </ul>
      
      <p>Effective loan management requires a system that supports the entire lifecycle, not just the beginning and end of the lending process.</p>
    `,
    date: 'August 10, 2026',
    author: 'Micropoint Systems',
    category: 'Lending',
    readTime: '6 min read',
    featured: false,
    icon: FaLightbulb
  },
  {
    id: 3,
    title: 'Automating Payment Reconciliation for SACCOs and MFIs',
    excerpt: 'How bank and mobile-money integrations can reduce manual matching, improve visibility and speed up posting.',
    content: `
      <p>Payment reconciliation is one of the most time-consuming operational tasks for SACCOs and microfinance institutions. Manual matching of incoming payments to member accounts can be slow, error-prone, and expensive.</p>
      
      <h3>The Challenge of Manual Reconciliation</h3>
      <p>Without automation, reconciliation involves:</p>
      <ul>
        <li>Downloading bank statements or transaction reports</li>
        <li>Manually matching payments to member accounts</li>
        <li>Identifying and resolving mismatches</li>
        <li>Posting transactions to the core system</li>
        <li>Reconciling differences and errors</li>
      </ul>
      
      <h3>How Automation Works</h3>
      <p>Payment reconciliation automation connects your core system to payment channels:</p>
      <ul>
        <li>Bank APIs provide real-time transaction notifications</li>
        <li>M-PESA integration delivers payment confirmations</li>
        <li>Payment gateways send reconciliation data</li>
        <li>Automated matching rules reduce manual effort</li>
        <li>Exceptions are flagged for review</li>
      </ul>
      
      <h3>Benefits of Automation</h3>
      <ul>
        <li>Reduced manual effort and operating costs</li>
        <li>Faster posting and member service</li>
        <li>Improved accuracy and reduced errors</li>
        <li>Better visibility into cash position</li>
        <li>Enhanced audit trail and compliance</li>
      </ul>
      
      <p>Automating payment reconciliation is one of the most impactful steps a financial institution can take toward operational efficiency.</p>
    `,
    date: 'August 5, 2026',
    author: 'Micropoint Systems',
    category: 'Automation',
    readTime: '5 min read',
    featured: false,
    icon: FaCloud
  },
  {
    id: 4,
    title: 'Bank Integration: APIs, Notifications and Transaction Reconciliation',
    excerpt: 'A practical explanation of how financial institutions can connect core systems to banks and turn transaction feeds into useful business workflows.',
    content: `
      <p>Bank integration is becoming essential for financial institutions. Connecting your core system to banking platforms enables real-time transaction visibility, automated reconciliation, and improved member service.</p>
      
      <h3>What Bank Integration Enables</h3>
      <p>With proper bank connectivity, your institution can:</p>
      <ul>
        <li>Receive real-time transaction notifications from banks</li>
        <li>Automatically match incoming payments to member accounts</li>
        <li>Reconcile bank statements with internal records</li>
        <li>Process payments and collections efficiently</li>
        <li>Provide members with up-to-date account information</li>
      </ul>
      
      <h3>Types of Bank Integration</h3>
      <ul>
        <li><strong>Transaction Notifications:</strong> Real-time alerts for incoming and outgoing transactions</li>
        <li><strong>Payment Processing:</strong> Initiate and confirm payments through bank APIs</li>
        <li><strong>Account Information:</strong> Access account balances and transaction history</li>
        <li><strong>Cheque Processing:</strong> Remote cheque scanning and truncation</li>
        <li><strong>Collections:</strong> Automated payment collection and matching</li>
      </ul>
      
      <h3>Integration Experience</h3>
      <p>Micropoint has delivered integrations with major banks including:</p>
      <ul>
        <li>NCBA Bank — account-level transaction notification</li>
        <li>Co-operative Bank — remote cheque scanning and truncation</li>
        <li>Family Bank — cheque truncation for microfinance/SACCO clients</li>
        <li>Equity Bank — integration projects</li>
        <li>KCB — integration currently being finalized</li>
      </ul>
      
      <p>Bank integration turns transaction feeds into useful business workflows that reduce manual effort and improve accuracy.</p>
    `,
    date: 'July 28, 2026',
    author: 'Micropoint Systems',
    category: 'Integration',
    readTime: '7 min read',
    featured: false,
    icon: FaShieldAlt
  },
  {
    id: 5,
    title: 'ISO 8583: An Established Standard That Still Matters in Financial Transaction Processing',
    excerpt: 'A plain-English explanation of ISO 8583 and where established financial transaction messaging still fits alongside modern APIs.',
    content: `
      <p>ISO 8583 is the international standard for financial transaction card originated interchange messaging. Despite the rise of modern APIs, ISO 8583 remains relevant for many payment processing environments.</p>
      
      <h3>What is ISO 8583?</h3>
      <p>ISO 8583 defines the message format and communication flow for financial transactions. It's the backbone of:</p>
      <ul>
        <li>ATM transaction processing and switching</li>
        <li>POS (Point of Sale) payment authorization</li>
        <li>Card-based payment networks</li>
        <li>EFT (Electronic Funds Transfer) switching</li>
      </ul>
      
      <h3>Why It Still Matters</h3>
      <p>Despite the availability of modern REST APIs, ISO 8583 continues to be important because:</p>
      <ul>
        <li>It provides a standardized, reliable messaging format</li>
        <li>It supports high-volume, low-latency transaction processing</li>
        <li>It is deeply embedded in existing payment infrastructure</li>
        <li>It ensures interoperability between different systems</li>
      </ul>
      
      <h3>ISO 8583 and Modern APIs</h3>
      <p>The choice between ISO 8583 and modern APIs depends on the specific requirements:</p>
      <ul>
        <li>ISO 8583 remains the standard for high-volume switching environments</li>
        <li>REST APIs are often used for integration with newer financial applications</li>
        <li>Many organizations use both: ISO 8583 for core switching and APIs for digital channels</li>
      </ul>
      
      <p>Micropoint has historical experience with ISO 8583-based financial transaction messaging, including payment-switching environments.</p>
    `,
    date: 'July 20, 2026',
    author: 'Micropoint Systems',
    category: 'Technical',
    readTime: '5 min read',
    featured: false,
    icon: FaMobileAlt
  }
];

const categories = [
  { id: 'All', label: 'All' },
  { id: 'Guide', label: '📖 Guides' },
  { id: 'Lending', label: '💰 Lending' },
  { id: 'Automation', label: '⚡ Automation' },
  { id: 'Integration', label: '🔗 Integration' },
  { id: 'Technical', label: '⚙️ Technical' },
];

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find(post => post.featured);

  const openModal = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPost(null), 300);
  };

  const handleShare = (platform) => {
    if (!selectedPost) return;
    
    const url = window.location.href;
    const title = encodeURIComponent(selectedPost.title);
    let shareUrl = '';

    switch(platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=Check out this article: ${url}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
        });
        return;
      default:
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=500');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-blue-100">
              Insights & Resources
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
              Practical <span className="font-semibold text-blue-600">Insights</span>
            </h1>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm">
              Practical guidance, product information and real-world experience from Micropoint Systems.
            </p>
          </motion.div>
        </div>

        {/* ─── Search & Filter ─── */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-3 items-center">
            <div className="relative w-full md:w-64 flex-shrink-0">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 pl-10 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-x-auto scrollbar-hide">
              <div className="flex gap-1.5 min-w-max pb-1">
                {categories.map((category) => {
                  const isActive = selectedCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-slate-500 hover:text-blue-600 hover:bg-slate-50 border border-slate-200'
                      }`}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Results Count ─── */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{filteredPosts.length}</span> articles
          </p>
          {selectedCategory !== 'All' && (
            <button
              onClick={() => setSelectedCategory('All')}
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1 font-medium"
            >
              <FaTimes className="text-xs" /> Clear filter
            </button>
          )}
        </div>

        {/* ─── Featured Post ─── */}
        {featuredPost && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 cursor-pointer"
            onClick={() => openModal(featuredPost)}
          >
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all group">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="h-56 lg:h-auto relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-8">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                  <div className="relative z-10 text-center text-white">
                    {featuredPost.icon && (
                      <featuredPost.icon className="text-6xl mb-4" />
                    )}
                    <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium border border-white/20">
                      Featured Article
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block bg-blue-50 text-blue-700 text-[10px] px-2.5 py-0.5 rounded-full font-medium">
                      {featuredPost.category}
                    </span>
                    <span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] px-2.5 py-0.5 rounded-full font-semibold">
                      ⭐ Featured
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors leading-tight">
                    {featuredPost.title}
                  </h2>
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2">{featuredPost.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt className="text-blue-500 text-[10px]" />
                      {featuredPost.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaUser className="text-blue-500 text-[10px]" />
                      {featuredPost.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaBookOpen className="text-blue-500 text-[10px]" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <div className="mt-4 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors inline-flex items-center gap-1 group-hover:gap-2">
                    Read Full Article <FaArrowRight className="text-xs" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── Blog Grid ─── */}
        {filteredPosts.filter(post => !post.featured).length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Articles Found</h3>
            <p className="text-slate-500">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredPosts.filter(post => !post.featured).map((post) => {
              const Icon = post.icon;
              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: post.id * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => openModal(post)}
                >
                  <div className="bg-white rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all h-full flex flex-col overflow-hidden">
                    <div className={`h-40 ${post.id === 2 ? 'bg-gradient-to-br from-amber-500 to-orange-600' : post.id === 3 ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : post.id === 4 ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gradient-to-br from-purple-500 to-indigo-600'} flex items-center justify-center relative overflow-hidden`}>
                      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                      <div className="relative z-10 flex flex-col items-center">
                        {Icon && <Icon className="text-5xl text-white drop-shadow-lg" />}
                        <span className="text-white/80 text-xs mt-2 font-medium bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-slate-500 text-sm mb-3 line-clamp-2 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-100">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <FaCalendarAlt className="text-[10px]" /> {post.date}
                        </span>
                        <span className="text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors inline-flex items-center gap-1 group-hover:gap-2">
                          Read More <FaArrowRight className="text-xs" />
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* ─── CTA Section ─── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center text-white mt-12"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 border border-white/10">
              <FaLightbulb className="text-xl text-blue-400" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-2 text-white">
              Need help choosing the <span className="text-blue-400">right solution</span>?
            </h3>
            <p className="text-white/50 text-sm mb-6 max-w-lg mx-auto">
              Tell us what you are trying to achieve and our team will help you identify the right Micropoint solution.
            </p>
            <Link 
              to="/contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold text-sm transition-all hover:shadow-lg hover:shadow-blue-600/30 hover:-translate-y-0.5"
            >
              Talk to Our Team
            </Link>
          </div>
        </motion.div>

      </div>

      {/* ─── Modal ─── */}
      <AnimatePresence>
        {isModalOpen && selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cover Image */}
              <div className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                <div className="relative z-10">
                  {selectedPost.icon && <selectedPost.icon className="text-5xl text-white drop-shadow-lg" />}
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center transition-all hover:scale-105 hover:shadow-xl"
              >
                <FaTimes className="text-slate-500 text-sm" />
              </button>

              {/* Modal Content */}
              <div className="px-6 md:px-8 pb-8 -mt-8 relative z-10">
                <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="inline-block bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                        {selectedPost.category}
                      </span>
                      {selectedPost.featured && (
                        <span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                          ⭐ Featured
                        </span>
                      )}
                    </div>
                    
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">
                      {selectedPost.title}
                    </h2>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-2">
                        <FaUser className="text-blue-500 text-xs" />
                        {selectedPost.author}
                      </span>
                      <span className="flex items-center gap-2">
                        <FaCalendarAlt className="text-blue-500 text-xs" />
                        {selectedPost.date}
                      </span>
                      <span className="flex items-center gap-2">
                        <FaBookOpen className="text-blue-500 text-xs" />
                        {selectedPost.readTime}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-200 mb-6" />

                  {/* Content */}
                  <div className="prose prose-slate max-w-none">
                    <div 
                      className="space-y-4 text-slate-700 text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: selectedPost.content.replace(
                        /<h3>/g, '<h3 class="text-xl font-bold text-slate-900 mt-8 mb-3">'
                      ).replace(
                        /<ul>/g, '<ul class="space-y-2 my-4">'
                      ).replace(
                        /<li>/g, '<li class="flex items-start gap-2 text-slate-700">'
                      ).replace(
                        /<p>/g, '<p class="text-slate-700 leading-relaxed mb-4">'
                      ).replace(
                        /<strong>/g, '<strong class="font-semibold text-slate-900">'
                      ) }}
                    />
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-200 my-6" />

                  {/* Share Section */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm text-slate-500 font-medium">Share this article:</span>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleShare('twitter')}
                        className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#1DA1F2] hover:text-white text-slate-500 transition-all flex items-center justify-center hover:scale-105"
                        title="Share on Twitter"
                      >
                        <FaTwitter className="text-sm" />
                      </button>
                      <button 
                        onClick={() => handleShare('linkedin')}
                        className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#0A66C2] hover:text-white text-slate-500 transition-all flex items-center justify-center hover:scale-105"
                        title="Share on LinkedIn"
                      >
                        <FaLinkedin className="text-sm" />
                      </button>
                      <button 
                        onClick={() => handleShare('email')}
                        className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#EA4335] hover:text-white text-slate-500 transition-all flex items-center justify-center hover:scale-105"
                        title="Share via Email"
                      >
                        <FaEnvelope className="text-sm" />
                      </button>
                      <button 
                        onClick={() => handleShare('copy')}
                        className={`w-10 h-10 rounded-full transition-all flex items-center justify-center hover:scale-105 ${
                          shareSuccess 
                            ? 'bg-green-500 text-white' 
                            : 'bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-500'
                        }`}
                        title="Copy link"
                      >
                        {shareSuccess ? <FaCheck className="text-sm" /> : <FaLink className="text-sm" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}